import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative text-white" data-testid="footer">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=2000)',
          filter: 'brightness(0.25) saturate(0.85)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1F1A1A]/85 via-[#2A0000]/90 to-[#1F1A1A]/95" />

      <div className="relative container mx-auto px-4 sm:px-6 md:px-8 py-10 md:py-16">

        {/* ── Top: Brand + grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">

          {/* Column 1: Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_ae32ccb0-3c5f-414a-b46c-cfd49c04a91f/artifacts/kjz9vxob_556719997_17915806479189788_8925177708886366329_n.jpg"
                alt="Ahmedabad NX"
                className="h-14 w-14 rounded-sm object-cover flex-shrink-0"
              />
              <h3 className="font-heading text-xl font-bold text-[#E5A93C] leading-tight">AHMEDABAD NX</h3>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed max-w-xs">
              Premium ladies ethnic wear boutique specializing in sarees, gowns, and designer collections since 2014.
            </p>

            {/* Social icons — visible on mobile below brand */}
            <div className="flex gap-3 mt-5 lg:hidden">
              <a href="https://www.instagram.com/ahmedabadnx2014" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-[#E5A93C]/10 hover:bg-[#E5A93C] rounded-full flex items-center justify-center transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://wa.me/919538769669" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-[#E5A93C]/10 hover:bg-[#E5A93C] rounded-full flex items-center justify-center transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-[#E5A93C]/10 hover:bg-[#E5A93C] rounded-full flex items-center justify-center transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="font-heading text-base font-semibold text-[#E5A93C] mb-4 uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About Us' },
                { to: '/collections', label: 'Collections' },
                { to: '/blogs', label: 'Blogs' },
                { to: '/contact', label: 'Contact' },
              ].map(({ to, label }) => (
                <li key={label}>
                  <Link to={to} className="text-sm text-gray-300 hover:text-[#E5A93C] transition-colors flex items-center gap-1.5 group">
                    <span className="w-0 group-hover:w-2 h-px bg-[#E5A93C] transition-all duration-200 overflow-hidden" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Social — desktop only */}
          <div className="hidden lg:block">
            <h4 className="font-heading text-base font-semibold text-[#E5A93C] mb-4 uppercase tracking-wider">Follow Us</h4>
            <div className="flex gap-3">
              <a href="https://www.instagram.com/ahmedabadnx2014" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-[#E5A93C]/10 hover:bg-[#E5A93C] rounded-full flex items-center justify-center transition-colors"
                data-testid="instagram-link">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://wa.me/919538769669" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-[#E5A93C]/10 hover:bg-[#E5A93C] rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-[#E5A93C]/10 hover:bg-[#E5A93C] rounded-full flex items-center justify-center transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="font-heading text-base font-semibold text-[#E5A93C] mb-4 uppercase tracking-wider">Contact Info</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#E5A93C] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 leading-snug">Gokul Road, Hubli, Karnataka, India</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#E5A93C] flex-shrink-0" />
                <a href="tel:+917760591999" className="text-gray-300 hover:text-[#E5A93C] transition-colors">+91 7760591999</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#E5A93C] flex-shrink-0" />
                <a href="mailto:ahmedabad44@gmail.com" className="text-gray-300 hover:text-[#E5A93C] transition-colors break-all">ahmedabad44@gmail.com</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 text-[#E5A93C] flex-shrink-0 mt-0.5" />
                <div className="text-gray-300 leading-snug">
                  <p>Monday – Sunday: 10:30 AM – 9:00 PM</p>  
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* ── Bottom Bar ── */}
        <div className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-gray-700/60">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

            {/* Copyright */}
            <p className="text-xs sm:text-sm text-gray-400 text-center sm:text-left">
              &copy; {new Date().getFullYear()} Ahmedabad NX. All rights reserved. | Established 2014
            </p>

            {/* Right side */}
            <div className="flex items-center justify-center sm:justify-end gap-4">
              <a href="https://x7ittechnologies.com" target="_blank" rel="noopener noreferrer">
                <span className="text-xs sm:text-sm text-gray-400">
                  Developed by{' '}
                  <span className="text-[#E5A93C] hover:text-orange-500 font-semibold transition-colors">
                    X7 IT Technologies
                  </span>
                </span>
              </a>

              <button
                onClick={scrollToTop}
                className="flex items-center gap-1 text-xs sm:text-sm text-gray-400 hover:text-[#E5A93C] transition-colors"
                data-testid="back-to-top"
              >
                <span>Top</span>
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;